import * as React from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useBookingT } from "@/hooks/use-translations"
import { DocumentViewer } from "@/pages/legal-documents/LegalDocumentsPage"
import { ProductActivationDialog } from "@/pages/legal-documents/ProductActivationDialog"
import {
  activateConnection,
  deleteConnection,
  enableRealtime,
  fetchEmployees,
  HrisApiError,
  OAUTH_PROVIDERS,
  pauseConnection,
  prefetchTabs,
  type HrisConnection,
  type HrisEmployeeFilter,
} from "@/features/integrations/hris/hrisApi"
import { useHrisConnection } from "@/features/integrations/hris/useHrisConnection"
import { HrisConnectCard } from "./HrisConnectCard"
import { HRIS_CONNECTION_EVENT } from "./HrisHeaderAction"
import { HrisOAuthConnectCard } from "./HrisOAuthConnectCard"
import { HrisEmployeesView } from "./HrisEmployeesView"
import { HrisHistoryCard } from "./HrisHistoryCard"
import { HrisExpensesCard } from "./HrisExpensesCard"
import { HrisPurchasesCard } from "./HrisPurchasesCard"
import { HrisLeaveOffersCard } from "./HrisLeaveOffersCard"
import { HrisLegalCard } from "./HrisLegalCard"
import { HrisMappingCard } from "./HrisMappingCard"
import { HrisRunCard } from "./HrisRunCard"
import { HrisStatusCard } from "./HrisStatusCard"

/** Tabs of the HR-system page; each has its own URL (/integrations/lucca/settings). */
export const HRIS_VIEWS = ["overview", "employees", "settings", "agreement", "expenses"] as const

export type HrisView = (typeof HRIS_VIEWS)[number]

type Props = {
  provider: string
  providerName: string
  view: HrisView
  employeesFilter?: HrisEmployeeFilter
  onNavigate: (view: HrisView, filter?: HrisEmployeeFilter) => void
}

/**
 * A first-party HR-system connection on the integration page, in tabs:
 * Overview (setup steps, switch on, the syncs), Employees (who is new or not
 * synced, add / skip), Settings (companies, which data, optional emails),
 * Agreement (Schedule E, the permission, the notice to employees) and
 * Expenses (writing expenses and purchases back). Replaces the generic
 * catalog connect flow for providers our own backend serves
 * (docs/integrations/hris/README.md).
 */
export function HrisPanel({ provider, providerName, view, employeesFilter, onNavigate }: Props) {
  const t = useBookingT()
  const state = useHrisConnection(provider)
  const { connection, run } = state
  const [busy, setBusy] = React.useState(false)
  const [legalOpen, setLegalOpen] = React.useState(false)
  const [legalKey, setLegalKey] = React.useState(0)
  const [reading, setReading] = React.useState<{ code: string; name: string; versionId?: number } | null>(null)
  const [replacingKey, setReplacingKey] = React.useState(false)
  const [confirmDisconnect, setConfirmDisconnect] = React.useState(false)
  const [attention, setAttention] = React.useState({ count: 0, held: 0 })

  const connectionId = connection?.id
  const runStatus = run?.status

  React.useEffect(() => {
    if (connectionId) prefetchTabs(connectionId)
  }, [connectionId])

  React.useEffect(() => {
    if (!connectionId || runStatus === "running") return
    fetchEmployees(connectionId, { tab: "attention" })
      .then((res) =>
        setAttention({ count: res.counts.attention, held: res.counts.heldArchives }),
      )
      .catch(() => setAttention({ count: 0, held: 0 }))
  }, [connectionId, runStatus])

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const updated = (next: HrisConnection) => {
    state.setConnection(next)
    window.dispatchEvent(new Event(HRIS_CONNECTION_EVENT))
  }

  if (state.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-40 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    )
  }

  if (state.error && !connection) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-error-700">{state.error}</CardContent>
      </Card>
    )
  }

  if ((!connection || replacingKey) && OAUTH_PROVIDERS.includes(provider)) {
    return (
      <HrisOAuthConnectCard
        provider={provider}
        providerName={providerName}
        onCancel={replacingKey ? () => setReplacingKey(false) : undefined}
        onConnected={(next) => {
          setReplacingKey(false)
          updated(next)
          toast.success(t("hris_connected", "Connected. Now match the legal entities to your companies."))
        }}
      />
    )
  }

  if (!connection || replacingKey) {
    return (
      <HrisConnectCard
        provider={provider}
        providerName={providerName}
        tenantUrl={connection?.tenantUrl}
        onCancel={replacingKey ? () => setReplacingKey(false) : undefined}
        onConnected={(next) => {
          setReplacingKey(false)
          updated(next)
          toast.success(t("hris_connected", "Connected. Now match the legal entities to your companies."))
        }}
      />
    )
  }

  const hasPreview = state.history.some((r) => r.kind === "preview" && r.status === "ok")

  return (
    <div className="space-y-6">
      <Tabs value={view} onValueChange={(v) => onNavigate(v as HrisView)}>
        <TabsList variant="underline" className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" variant="underline">
            {t("hris_tab_overview", "Overview")}
          </TabsTrigger>
          <TabsTrigger value="employees" variant="underline">
            {t("hris_tab_employees", "Employees")}
            {attention.count > 0 && (
              <span className="ml-1.5 rounded-full bg-warning-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">{attention.count}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" variant="underline">
            {t("hris_tab_settings", "Settings")}
          </TabsTrigger>
          <TabsTrigger value="agreement" variant="underline">
            {t("hris_tab_agreement", "Agreement")}
            {!state.legalComplete && (
              <span className="ml-1.5 rounded-full bg-warning-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">!</span>
            )}
          </TabsTrigger>
          {connection.writeBack && (
            <TabsTrigger value="expenses" variant="underline">
              {t("hris_tab_expenses", "Expenses and purchases")}
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {view === "employees" && <HrisEmployeesView connection={connection} initialFilter={employeesFilter} />}

      {view === "overview" && (
        <>
          <HrisStatusCard
            connection={connection}
            run={run}
            hasPreview={hasPreview}
            legalComplete={state.legalComplete}
            busy={busy}
            attentionCount={attention.count}
            heldArchives={attention.held}
            onOpenAttention={() => onNavigate("employees", "attention")}
            onEnableRealtime={async (oauthClient) => {
              setBusy(true)
              try {
                const next = await enableRealtime(connection.id, oauthClient)
                updated(next)
                if (next.updates.mode === "realtime") {
                  toast.success(t("hris_realtime_on", "Real-time updates are on."))
                  return true
                }
                toast.error(next.updates.webhookError ?? t("hris_realtime_failed", "Real-time updates could not be turned on."))
                return false
              } catch (e) {
                // The OAuth client form shows field errors itself.
                if (oauthClient) throw e
                toast.error((e as Error).message)
                return false
              } finally {
                setBusy(false)
              }
            }}
            onPreview={() => act(() => state.start("preview"))}
            onSync={() => act(() => state.start("sync"))}
            onActivate={() =>
              act(async () => {
                updated(await activateConnection(connection.id))
                await state.start("sync")
                toast.success(t("hris_activated", "The sync is on. The first run has started."))
              })
            }
            onPause={() => act(async () => updated(await pauseConnection(connection.id)))}
            onOpenLegal={() => setLegalOpen(true)}
            onScrollToMapping={() => onNavigate("settings")}
            onReplaceKey={() => setReplacingKey(true)}
            onDisconnect={() => setConfirmDisconnect(true)}
          />

          {run && (
            <HrisRunCard
              run={run}
              busy={busy}
              onConfirmArchives={() => act(() => state.start("sync", true))}
              onOpenEmployees={(filter) => onNavigate("employees", filter)}
            />
          )}

          <HrisHistoryCard runs={state.history} />
        </>
      )}

      {view === "settings" && (
        <>
          <HrisMappingCard connection={connection} onSaved={updated} />
          <HrisLeaveOffersCard connection={connection} refreshKey={legalKey} />
        </>
      )}

      {view === "agreement" && (
        <HrisLegalCard
          connectionId={connection.id}
          legalComplete={state.legalComplete}
          refreshKey={legalKey}
          onOpenActivation={() => setLegalOpen(true)}
          onRead={() => setReading({ code: "SCH_E", name: t("hris_legal_schedule", "Schedule E") })}
          onWithdrawn={() => {
            void state.reload()
            setLegalKey((k) => k + 1)
          }}
        />
      )}

      {view === "expenses" && connection.writeBack && (
        <>
          <HrisExpensesCard connection={connection} refreshKey={legalKey} />
          <HrisPurchasesCard connection={connection} refreshKey={legalKey} />
        </>
      )}

      {legalOpen && (
        <ProductActivationDialog
          productCode="HRIS"
          productName={t("legal_product_hris", "the HR system integration")}
          open
          onOpenChange={(open) => {
            if (open) return
            setLegalOpen(false)
            void state.reloadLegal()
            setLegalKey((k) => k + 1)
          }}
          onComplete={() => {
            void state.reloadLegal()
            setLegalKey((k) => k + 1)
          }}
          onRead={setReading}
        />
      )}
      <DocumentViewer target={reading} sample={false} onOpenChange={(open) => !open && setReading(null)} />

      <Dialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("hris_disconnect_title", "Disconnect {name}?").replace("{name}", providerName)}</DialogTitle>
            <DialogDescription className="space-y-2 text-neutral-800">
              <span className="block">
                {OAUTH_PROVIDERS.includes(provider)
                  ? t(
                      "hris_disconnect_text_oauth",
                      "The sync stops and the stored access and copies of HR data are deleted. Employees already in Travel Code stay.",
                    )
                  : t(
                      "hris_disconnect_text",
                      "The sync stops and the stored key and copies of HR data are deleted. Employees already in Travel Code stay.",
                    )}
              </span>
              {OAUTH_PROVIDERS.includes(provider) && (
                <span className="block">
                  {t(
                    "hris_disconnect_uninstall",
                    "{name} has no way for us to withdraw the app ourselves: to remove it completely, a {name} admin uninstalls Travel Code in {name} › Marketplace › Manage apps.",
                  )
                    .split("{name}")
                    .join(providerName)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDisconnect(false)}>
              {t("hris_cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                act(async () => {
                  try {
                    await deleteConnection(connection.id)
                  } catch (e) {
                    // Already removed elsewhere (another tab, another admin): nothing left to disconnect.
                    if (!(e instanceof HrisApiError && e.status === 404)) throw e
                  }
                  setConfirmDisconnect(false)
                  await state.reload()
                  window.dispatchEvent(new Event(HRIS_CONNECTION_EVENT))
                })
              }
            >
              {t("hris_btn_disconnect", "Disconnect")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
